import { createTanstackQueryUtils } from "@orpc/tanstack-query";
import {
  type ApiOutputs,
  visitNotesSchema,
  visitRecordingsSchema,
  visitVersionSchema,
} from "@rosc/contracts";
import { useEffect, useRef } from "react";
import { referenceClient } from "@/lib/orpc/client";
import { isInaccessible, type VisitScope } from "@/lib/visit-queries";
import { checkPublicPage, publicTraversalError } from "./public-pagination";

type Page<T> = { items: T[]; nextCursor: string | null };
function checkPage<T extends { public_id: string; visitPublicId: string }>(
  page: Page<T>,
  id: string,
  cursor?: string,
): Page<T> {
  if (page.items.some((item) => item.visitPublicId !== id))
    throw Error("Invalid content parent");
  return checkPublicPage(page, cursor);
}
const orpc = createTanstackQueryUtils(referenceClient, {
  scoped: {
    listVisitRecordings: {
      infiniteInterceptors: [
        async (options) => {
          if (typeof options.input === "symbol")
            throw new Error("Missing content input");
          return checkPage(
            visitRecordingsSchema.parse(await options.next()),
            options.input.params.public_id,
            options.input.query?.cursor,
          );
        },
      ],
    },
    listVisitNotes: {
      infiniteInterceptors: [
        async (options) => {
          if (typeof options.input === "symbol")
            throw new Error("Missing content input");
          return checkPage(
            visitNotesSchema.parse(await options.next()),
            options.input.params.public_id,
            options.input.query?.cursor,
          );
        },
      ],
    },
    getVisitVersion: {
      queryInterceptors: [
        async (options) => {
          if (typeof options.input === "symbol")
            throw new Error("Missing version input");
          const version = visitVersionSchema.parse(await options.next());
          if (
            version.visitPublicId !== options.input.params.public_id ||
            version.public_id !== options.input.params.versionPublicId
          )
            throw new Error("Invalid version parent");
          return version;
        },
      ],
    },
  },
});
export const contentKey = (
  scope: VisitScope,
  id: string,
): readonly unknown[] => [
  "visit-reads",
  scope.uid,
  scope.accountPublicId,
  scope.generation,
  "related",
  id,
];
export const recordingOptions = (scope: VisitScope, id: string) =>
  orpc.listVisitRecordings.infiniteOptions({
    queryKey: [...contentKey(scope, id), "recordings"],
    input: (cursor: string | undefined) => ({
      params: { public_id: String(id) },
      query: { limit: "100", ...(cursor ? { cursor } : {}) },
    }),
    initialPageParam: undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    context: { expectedUserUid: scope.uid },
    retry: false,
  });
export const noteOptions = (scope: VisitScope, id: string) =>
  orpc.listVisitNotes.infiniteOptions({
    queryKey: [...contentKey(scope, id), "notes"],
    input: (cursor: string | undefined) => ({
      params: { public_id: String(id) },
      query: { limit: "100", ...(cursor ? { cursor } : {}) },
    }),
    initialPageParam: undefined,
    getNextPageParam: (page) => page.nextCursor ?? undefined,
    context: { expectedUserUid: scope.uid },
    retry: false,
  });
export const versionOptions = (
  scope: VisitScope,
  id: string,
  versionPublicId: string,
) =>
  orpc.getVisitVersion.queryOptions({
    queryKey: [...contentKey(scope, id), "version", String(versionPublicId)],
    input: {
      params: {
        public_id: String(id),
        versionPublicId: String(versionPublicId),
      },
    },
    context: { expectedUserUid: scope.uid },
    retry: false,
  });
// Retain only a completed traversal while background refreshes assemble new pages.
export function useCompleteContent<T extends { public_id: string }>(
  query: {
    data?: { pages: Page<T>[] };
    isSuccess: boolean;
    isFetching: boolean;
    hasNextPage: boolean;
    isError: boolean;
    error: unknown;
    fetchNextPage: (options: { cancelRefetch: boolean }) => Promise<unknown>;
  },
  enabled: boolean,
  identity: string,
): T[] {
  const traversalError = publicTraversalError(query.data?.pages);
  const complete = useRef<
    | {
        identity: string;
        data: typeof query.data;
        items: T[];
      }
    | undefined
  >(undefined);
  if (
    !enabled ||
    complete.current?.identity !== identity ||
    isInaccessible(query.error)
  )
    complete.current = undefined;
  if (
    enabled &&
    !traversalError &&
    query.isSuccess &&
    !query.isFetching &&
    !query.hasNextPage &&
    query.data &&
    complete.current?.data !== query.data
  ) {
    complete.current = {
      identity,
      data: query.data,
      items: query.data.pages.flatMap((page) => page.items),
    };
  }
  const { hasNextPage, isFetching, isError, fetchNextPage } = query;
  useEffect(() => {
    if (enabled && hasNextPage && !isFetching && !isError && !traversalError)
      void fetchNextPage({ cancelRefetch: false });
  }, [
    enabled,
    hasNextPage,
    isFetching,
    isError,
    fetchNextPage,
    traversalError,
  ]);
  return complete.current?.items ?? [];
}
export type VisitRecording = ApiOutputs["listVisitRecordings"]["items"][number];
export type VisitNote = ApiOutputs["listVisitNotes"]["items"][number];
export type VisitVersion = ApiOutputs["getVisitVersion"];
